import React, { createContext, useState, useContext, useRef, useEffect } from 'react';
import { resolveUrl } from '../utils/urlUtils';

const AudioPlayerContext = createContext();

export const AudioPlayerProvider = ({ children }) => {
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const audioRef = useRef(new Audio());

  // Set up audio element
  useEffect(() => {
    const audio = audioRef.current;
    
    // Event listeners
    const setAudioData = () => {
      setDuration(audio.duration);
      setCurrentTime(audio.currentTime);
    };
    
    const setAudioTime = () => setCurrentTime(audio.currentTime);
    const setAudioEnd = () => setIsPlaying(false);
    
    // Add event listeners
    audio.addEventListener('loadeddata', setAudioData);
    audio.addEventListener('timeupdate', setAudioTime);
    audio.addEventListener('ended', setAudioEnd);
    
    // Set volume
    audio.volume = volume;
    
    // Cleanup
    return () => {
      audio.removeEventListener('loadeddata', setAudioData);
      audio.removeEventListener('timeupdate', setAudioTime);
      audio.removeEventListener('ended', setAudioEnd);
    };
  }, [volume]);

  // Handle track changes
  useEffect(() => {
    const audio = audioRef.current;
    
    if (currentTrack) {
      // Make sure we resolve the URL to use the correct base URL
      audio.src = resolveUrl(currentTrack.url);
      audio.load();
      if (isPlaying) {
        audio.play();
      }
    } else {
      audio.pause();
      setIsPlaying(false);
    }
  }, [currentTrack, isPlaying]);

  // Play/pause control
  const togglePlay = () => {
    if (!currentTrack) return;
    
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  // Seek control
  const handleSeek = (newTime) => {
    if (!currentTrack) return;
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  // Volume control
  const handleVolumeChange = (newVolume) => {
    setVolume(newVolume);
    audioRef.current.volume = newVolume;
  };

  // Set track from external components
  const playTrack = (track) => {
    // Ensure the URL is resolved when the track is set
    const processedTrack = track ? {
      ...track,
      url: resolveUrl(track.url)
    } : null;
    
    setCurrentTrack(processedTrack);
    setIsPlaying(true);
  };

  return (
    <AudioPlayerContext.Provider
      value={{
        currentTrack,
        isPlaying,
        duration,
        currentTime,
        volume,
        togglePlay,
        handleSeek,
        handleVolumeChange,
        playTrack
      }}
    >
      {children}
    </AudioPlayerContext.Provider>
  );
};

// Hook to use the audio player context
export const useAudioPlayer = () => {
  const context = useContext(AudioPlayerContext);
  if (!context) {
    throw new Error('useAudioPlayer must be used within an AudioPlayerProvider');
  }
  return context;
};
